import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { tryFetchWithAuth } from 'utils/fetchWithAuth';

const ApiKeyContext = createContext({
    plaintextKey: null,
    prefix: null,
    hasKey: false,
    loading: false,
    error: null,
    refresh: async () => {},
    createOrRotate: async () => ({ ok: false }),
});

export function ApiKeyProvider({ children }) {
    const { user, session } = useAuth();
    // plaintextKey is only ever populated in-memory after a successful create/rotate
    // in the current tab. It is intentionally never persisted to storage.
    const [plaintextKey, setPlaintextKey] = useState(null);
    const [prefix, setPrefix] = useState(null);
    const [hasKey, setHasKey] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        if (!user) return;
        setLoading(true);
        setError(null);
        const { data, error: fetchError } = await tryFetchWithAuth('/api_keys/me');
        if (fetchError) {
            setError(fetchError);
            setHasKey(false);
            setPrefix(null);
        } else if (data?.has_key) {
            setHasKey(true);
            setPrefix(data.prefix || null);
        } else {
            setHasKey(false);
            setPrefix(null);
        }
        setLoading(false);
    }, [user]);

    useEffect(() => {
        if (session && user) {
            refresh();
        } else {
            setPlaintextKey(null);
            setPrefix(null);
            setHasKey(false);
            setError(null);
        }
    }, [session, user, refresh]);

    const createOrRotate = useCallback(async () => {
        const { data, error: fetchError } = await tryFetchWithAuth('/api_keys', { method: 'POST' });
        if (fetchError || !data?.api_key) {
            return { ok: false, error: fetchError || 'No api_key returned' };
        }
        setPlaintextKey(data.api_key);
        setPrefix(data.prefix || null);
        setHasKey(true);
        return { ok: true, apiKey: data.api_key };
    }, []);

    const value = useMemo(() => ({
        plaintextKey,
        prefix,
        hasKey,
        loading,
        error,
        refresh,
        createOrRotate,
    }), [plaintextKey, prefix, hasKey, loading, error, refresh, createOrRotate]);

    return <ApiKeyContext.Provider value={value}>{children}</ApiKeyContext.Provider>;
}

export function useApiKey() {
    return useContext(ApiKeyContext);
}
